import type { ReactNode } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextLink from 'LuxKit/TextLink'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'

type AccommodationRoomTileKnownMessage =
  'loading' |
  'members-exclusive' |
  'sold-out-on-dates' |
  'sold-out' |
  'exceeded-capacity' |
  'sold-out-on-dates-or-exceeded-capacity' |
  'available-on-dates' |
  'available-on-dates-variant' |
  'available-for-some-rooms' |
  'available-if-quick-filters-relaxed'

interface Props {
  messageType: AccommodationRoomTileKnownMessage
  offer: App.HotelOffer | App.BedbankOffer
  onMultiRoomModeClick?: () => void
  onChangeOccupancyClick?: () => void
  onChangeDatesClick?: () => void
  onClearQuickFiltersClick?: () => void
}

function AccommodationRoomTileMessage(props: Props) {
  const {
    messageType,
    offer,
    onMultiRoomModeClick,
    onChangeOccupancyClick,
    onChangeDatesClick,
    onClearQuickFiltersClick,
  } = props

  const intl = useIntl()
  const { formatSaleUnitLabel, getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const saleUnitLabel = formatSaleUnitLabel(offer.saleUnit)
  const saleUnitPlural = getSaleUnitPluralNoun(offer.saleUnit)

  const renderTryDifferentDatesLink = useCallback((chunks: ReactNode) => {
    if (!onChangeDatesClick) return <>{chunks}</>
    return <TextLink onClick={onChangeDatesClick}>{chunks}</TextLink>
  }, [onChangeDatesClick])

  const renderAddMoreRoomsLink = useCallback((chunks: ReactNode) => {
    if (!onChangeOccupancyClick) return <>{chunks}</>
    return <TextLink onClick={onChangeOccupancyClick}>{chunks}</TextLink>
  }, [onChangeOccupancyClick])

  const renderChooseIndividuallyLink = useCallback((chunks: ReactNode) => {
    if (!onMultiRoomModeClick) return <>{chunks}</>
    return <TextLink onClick={onMultiRoomModeClick}>{chunks}</TextLink>
  }, [onMultiRoomModeClick])

  const renderClearFiltersLink = useCallback((chunks: ReactNode) => {
    if (!onClearQuickFiltersClick) return <>{chunks}</>
    return <TextLink onClick={onClearQuickFiltersClick}>{chunks}</TextLink>
  }, [onClearQuickFiltersClick])

  const messageLabel = useMemo(() => {
    switch (messageType) {
      case 'members-exclusive':
        return intl.formatMessage({
          id: '5oe3T8UMAnd6dq4HDe4H',
          defaultMessage: 'Sign up to access exclusive rates',
        })
      case 'sold-out-on-dates':
        return onChangeDatesClick
          ? intl.formatMessage(
              {
                id: '0iOleaTVMB5cyFhwUgu_',
                defaultMessage: 'This {saleUnitLong} is sold out on your dates. Choose another {saleUnit} or <link>select different dates</link>.',
              },
              {
                saleUnitLong: offer.saleUnitLong,
                saleUnit: saleUnitLabel,
                link: renderTryDifferentDatesLink,
              },
            )
          : intl.formatMessage(
              {
                id: '5KagClSaIa_U1bFqVgQS',
                defaultMessage: 'This {saleUnitLong} is sold out on your dates',
              },
              {
                saleUnitLong: offer.saleUnitLong,
              },
            )
      case 'sold-out':
        return intl.formatMessage(
          {
            id: 'jgJjOJPEDpQCkwsExg68',
            defaultMessage: 'This {saleUnitLong} is sold out',
          },
          {
            saleUnitLong: offer.saleUnitLong,
          },
        )
      case 'exceeded-capacity':
        return onChangeOccupancyClick
          ? intl.formatMessage(
              {
                id: 'hXoMX1IIIfTewMJvH_g6',
                defaultMessage: 'This {saleUnit} is not suitable for the number of selected guests. Choose another {saleUnit} or <link>add more {saleUnitPlural}</link>.',
              },
              {
                saleUnit: saleUnitLabel,
                saleUnitPlural,
                link: renderAddMoreRoomsLink,
              },
            )
          : intl.formatMessage(
              {
                id: 'Zuk3oAoBYcEkeyOLEUr3',
                defaultMessage: 'This {saleUnit} is not suitable for the number of selected guests.',
              },
              {
                saleUnit: saleUnitLabel,
              },
            )
      case 'sold-out-on-dates-or-exceeded-capacity':
        if (onChangeDatesClick && onChangeOccupancyClick) {
          return intl.formatMessage(
            {
              id: 'XOupeMO8-1RPhOts3Rj1',
              defaultMessage: 'This {saleUnit} is sold out on your dates or is not suitable for the number of selected guests. Choose another {saleUnit}, <datesLink>try different dates</datesLink> or <occupancyLink>add more {saleUnitPlural}</occupancyLink>.',
            },
            {
              saleUnit: saleUnitLabel,
              saleUnitPlural,
              datesLink: renderTryDifferentDatesLink,
              occupancyLink: renderAddMoreRoomsLink,
            },
          )
        }
        if (onChangeDatesClick) {
          return intl.formatMessage(
            {
              id: 'sryf2l2oN_KMTCAOf7sQ',
              defaultMessage: 'This {saleUnit} is sold out on your dates or is not suitable for the number of selected guests. Choose another {saleUnit} or <datesLink>try different dates</datesLink>.',
            },
            {
              saleUnit: saleUnitLabel,
              datesLink: renderTryDifferentDatesLink,
            },
          )
        }
        if (onChangeOccupancyClick) {
          return intl.formatMessage(
            {
              id: 'O73tuxtUyasvSgkWl5IJ',
              defaultMessage: 'This {saleUnit} is sold out on your dates or is not suitable for the number of selected guests. Choose another {saleUnit} or <occupancyLink>add more {saleUnitPlural}</occupancyLink>.',
            },
            {
              saleUnit: saleUnitLabel,
              saleUnitPlural,
              occupancyLink: renderAddMoreRoomsLink,
            },
          )
        }
        return intl.formatMessage(
          {
            id: 'P412-WePl8GWkjVAaH4_',
            defaultMessage: 'This {saleUnit} is sold out on your dates or is not suitable for the number of selected guests.',
          },
          {
            saleUnit: saleUnitLabel,
          },
        )
      case 'available-for-some-rooms':
        return onMultiRoomModeClick
          ? intl.formatMessage(
              {
                id: 'knJy06fsAfb2KMC1Z-EF',
                defaultMessage: 'This {saleUnit} type works for some {saleUnitPlural} in your group, but not all. To select it, try <link>choosing {saleUnitPlural} individually</link>.',
              },
              {
                saleUnit: saleUnitLabel,
                saleUnitPlural,
                link: renderChooseIndividuallyLink,
              },
            )
          : intl.formatMessage(
              {
                id: '24fQKyEolo3OAOxQyyv0',
                defaultMessage: 'This {saleUnit} type works for some {saleUnitPlural} in your group, but not all. To select it, try choosing {saleUnitPlural} individually.',
              },
              {
                saleUnit: saleUnitLabel,
                saleUnitPlural,
              },
            )
      case 'available-if-quick-filters-relaxed':
        return onClearQuickFiltersClick
          ? intl.formatMessage(
              {
                id: 'GOavDCvAotd9yz5wva6K',
                defaultMessage: 'No {saleUnitPlural} match these filters. <link>Clear filters</link>.',
              },
              {
                saleUnitPlural,
                link: renderClearFiltersLink,
              },
            )
          : intl.formatMessage(
              {
                id: 'Txz84QKM_qnU1L6lnU0Z',
                defaultMessage: 'No {saleUnitPlural} match these filters.',
              },
              {
                saleUnitPlural,
              },
            )
    }
  }, [messageType, intl, offer, onChangeDatesClick, onChangeOccupancyClick, onMultiRoomModeClick, onClearQuickFiltersClick, renderTryDifferentDatesLink, renderAddMoreRoomsLink, renderChooseIndividuallyLink, renderClearFiltersLink, saleUnitLabel, saleUnitPlural])

  if (!messageLabel) return null

  return <MessageBanner kind="informational" density="compact">
    {messageLabel}
  </MessageBanner>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
}), AccommodationRoomTileMessage)
